import { keycloak } from "@/utils/lib/keycloak";
import { AuthContext, Role, User } from "./authContext";
import { axiosInstance } from "@/utils/lib/axios";
import { useMemo, useState, useEffect } from "react";
import { Loading } from "@/components/ui/loading";

let isKeycloakInitialized = false;
let keycloakInitPromise: Promise<boolean> | null = null;

function initializeKeycloak() {
  if (!keycloakInitPromise) {
    keycloakInitPromise = keycloak
      .init({
        onLoad: "login-required",
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        console.info(
          `User is ${authenticated ? "authenticated" : "not authenticated"}`,
        );

        if (authenticated) {
          axiosInstance.interceptors.request.use(
            (config) => {
              return keycloak
                .updateToken()
                .then(() => {
                  config.headers.Authorization = `Bearer ${keycloak.token}`;
                  return config;
                })
                .catch(() => {
                  keycloak.login();
                  return Promise.reject(new Error("Unauthorized"));
                });
            },
            (error: Error) => {
              return Promise.reject(error);
            },
          );
        }
        isKeycloakInitialized = true;
        return authenticated;
      })
      .catch((e) => {
        console.error("Keycloak initialization failed:", e);
        return Promise.reject(e);
      });
  }
  return keycloakInitPromise;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(isKeycloakInitialized);

  useEffect(() => {
    if (!isKeycloakInitialized) {
      initializeKeycloak()
        .then(() => {
          setIsReady(true);
        })
        .catch((error) => {
          console.error("Failed to initialize Keycloak:", error);
        });
    }
  }, []);

  function getUser(): User {
    if (!isKeycloakInitialized || !keycloak.tokenParsed) {
      return {} as User;
    }
    const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
    return {
      id: keycloak.tokenParsed?.sub,
      email: keycloak.tokenParsed?.email,
      username: keycloak.tokenParsed?.preferred_username,
      firstName: keycloak.tokenParsed?.given_name,
      lastName: keycloak.tokenParsed?.family_name,
      roles: keycloak.tokenParsed?.resource_access?.[keycloakClientId]
        ?.roles as Role[],
    };
  }

  const value = useMemo(() => {
    return {
      login: keycloak.login,
      logout: keycloak.logout,
      getUser: getUser,
      isReady,
    };
  }, [isReady]);

  if (!isReady) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loading>Authenticating...</Loading>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
